import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Wallet as WalletIcon, Plus, Loader2, ArrowUpRight, ArrowDownRight, Copy, Check, Gift, ClipboardCheck, Zap } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { openRazorpayCheckout } from "@/lib/razorpay";
import { useAuth } from "@/lib/auth";

export default function Wallet() {
  const [balance, setBalance] = useState(0);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [topUpOpen, setTopUpOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [w, t, s] = await Promise.all([
        api.get("/wallet/me"),
        api.get("/wallet/transactions?limit=100"),
        api.get("/public/platform-settings"),
      ]);
      setBalance(w.data.coins || 0);
      setTxns(t.data.items || []);
      setSettings(s.data || {});
    } catch (err) { toast.error(apiError(err, "Could not load wallet")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const { user } = useAuth();
  const rupeesToCoins = (r) => Math.floor(Number(r || 0) * (settings.coins_per_rupee || 1));

  return (
    <DashboardLayout role={user?.role === "lab_admin" ? "lab_admin" : "patient"}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><WalletIcon className="w-6 h-6 text-efgh-green" /> Wallet</h1>
          <p className="text-ink/60">Use coins to generate smart reports · earn coins by referring friends</p>
        </div>
        <Button data-testid="top-up-btn" onClick={() => setTopUpOpen(true)} className="bg-ink hover:bg-ink/90 text-white"><Plus className="w-4 h-4 mr-2" /> Top-up wallet</Button>
      </div>

      <div className="mt-6 grid md:grid-cols-3 gap-4">
        <Card className="p-6 rounded-2xl md:col-span-2 bg-gradient-to-br from-efgh-green to-efgh-blue text-white" data-testid="balance-card">
          <div className="text-white/80 text-xs uppercase tracking-widest">Available balance</div>
          <div className="mt-2 font-heading font-black text-6xl leading-none">{loading ? "…" : balance}<span className="text-2xl align-top ml-1 opacity-80">coins</span></div>
          <div className="mt-2 text-sm text-white/85">≈ ₹{balance} · Each smart report costs {settings.report_cost_coins ?? 15} coins</div>
        </Card>
        <Card className="p-6 rounded-2xl" data-testid="how-to-earn">
          <div className="font-heading font-semibold text-ink flex items-center gap-2"><Gift className="w-4 h-4 text-efgh-yellow" /> Earn coins</div>
          <ul className="mt-3 text-sm text-ink/70 space-y-2">
            <li>+ {settings.signup_bonus_coins ?? 15} on signup <span className="text-[10px] text-ink/40">(welcome)</span></li>
            <li>+ 25 when a friend you referred generates their first report</li>
            <li>+ any amount via UPI top-up (1 coin = ₹1)</li>
          </ul>
        </Card>
      </div>

      <Card className="mt-6 p-0 rounded-2xl overflow-hidden" data-testid="txns-card">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div className="font-heading font-semibold text-ink">Transaction history</div>
          <div className="text-xs text-ink/50">{txns.length} entries</div>
        </div>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
        ) : txns.length === 0 ? (
          <div className="p-10 text-center text-sm text-ink/50">No transactions yet.</div>
        ) : (
          <div>
            {txns.map((t) => <TxnRow key={t.id} t={t} />)}
          </div>
        )}
      </Card>

      <TopUpDialog open={topUpOpen} onClose={() => { setTopUpOpen(false); load(); }} settings={settings} rupeesToCoins={rupeesToCoins} />
    </DashboardLayout>
  );
}

const REASON_LABELS = {
  signup_bonus: "Signup welcome bonus",
  referral_reward: "Referral reward",
  top_up_approved: "UPI top-up (approved)",
  top_up_pending: "UPI top-up (pending review)",
  top_up_rejected: "UPI top-up (rejected)",
  report_generation: "Smart report generated",
  admin_adjust: "Adjustment by admin",
};

function TxnRow({ t }) {
  const isCredit = t.type === "credit";
  const label = REASON_LABELS[t.reason] || t.reason;
  const badge = t.status === "pending" ? "pending" : t.status === "rejected" ? "rejected" : "";
  return (
    <div className="grid grid-cols-[auto_1fr_auto] gap-3 px-6 py-3 items-center border-b border-border/50 last:border-b-0" data-testid={`txn-${t.id}`}>
      <div className={`w-9 h-9 rounded-lg grid place-items-center ${isCredit ? "bg-success/10 text-success" : "bg-danger/10 text-danger"}`}>
        {isCredit ? <ArrowDownRight className="w-4 h-4" /> : <ArrowUpRight className="w-4 h-4" />}
      </div>
      <div className="min-w-0">
        <div className="text-sm text-ink font-medium">{label}</div>
        <div className="text-[11px] text-ink/50">{new Date(t.created_at).toLocaleString()} {badge && <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] ${badge === "pending" ? "bg-warning/15 text-warning" : "bg-danger/10 text-danger"}`}>{badge}</span>}</div>
      </div>
      <div className={`font-heading font-bold ${isCredit ? "text-success" : "text-danger"}`}>{isCredit ? "+" : "−"}{t.amount}</div>
    </div>
  );
}

function TopUpDialog({ open, onClose, settings, rupeesToCoins }) {
  const [amount, setAmount] = useState(150);
  const [utr, setUtr] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("pay"); // pay | confirm | done
  const [copied, setCopied] = useState(false);
  const [method, setMethod] = useState("razorpay"); // razorpay | upi
  const [rzpConfig, setRzpConfig] = useState(null);
  const { user } = useAuth();
  const upiId = settings.upi_id || "";
  const upiName = settings.upi_display_name || "EFGH Health";
  const payUrl = upiId ? `upi://pay?pa=${upiId}&pn=${encodeURIComponent(upiName)}&am=${amount}&cu=INR&tn=EFGH+wallet+top-up` : "";

  useEffect(() => {
    if (open && !rzpConfig) {
      api.get("/payments/config").then(r => setRzpConfig(r.data)).catch(() => setRzpConfig({ razorpay_enabled: false }));
    }
  }, [open, rzpConfig]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!utr.trim()) return toast.error("Please enter the UPI reference (UTR)");
    setSaving(true);
    try {
      await api.post("/wallet/top-up/request", { amount_rupees: amount, upi_utr: utr.trim(), notes: notes.trim() });
      setStep("done");
    } catch (err) { toast.error(apiError(err, "Could not submit request")); }
    finally { setSaving(false); }
  };

  const payRazorpay = async () => {
    if (amount < 10) { toast.error("Minimum ₹10"); return; }
    setSaving(true);
    // Close the underlying top-up dialog so Razorpay's modal has clean focus / no pointer conflicts.
    onClose();
    try {
      const { data: order } = await api.post("/payments/order", { amount_rupees: amount });
      const result = await openRazorpayCheckout({
        key_id: order.key_id,
        order_id: order.order_id,
        amount_paise: order.amount_paise,
        description: `EFGH Wallet Top-up · ₹${amount}`,
        prefill: {
          name: user?.full_name || "EFGH Patient",
          email: user?.email || "",
          contact: user?.phone || "9876543210",
        },
        theme_color: "#F97316",
      });
      if (!result) { setSaving(false); return; } // user dismissed
      const verifyRes = await api.post("/payments/verify", {
        razorpay_order_id: result.razorpay_order_id,
        razorpay_payment_id: result.razorpay_payment_id,
        razorpay_signature: result.razorpay_signature,
      });
      if (verifyRes.data.ok) {
        toast.success(`+${verifyRes.data.coins_credited || rupeesToCoins(amount)} coins added to your wallet`);
      }
    } catch (err) {
      toast.error(apiError(err, "Payment could not be completed"));
    } finally {
      setSaving(false);
    }
  };

  const copyUpi = () => { navigator.clipboard.writeText(upiId); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const razorpayEnabled = rzpConfig?.razorpay_enabled;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { onClose(); setStep("pay"); setUtr(""); setNotes(""); } }}>
      <DialogContent className="max-w-md" data-testid="top-up-dialog">
        <DialogHeader><DialogTitle>Top-up wallet</DialogTitle></DialogHeader>
        {step === "done" ? (
          <div className="space-y-3">
            <div className="p-6 bg-success/10 text-success text-center rounded-lg">
              <ClipboardCheck className="w-8 h-8 mx-auto mb-2" />
              <div className="font-heading font-semibold">
                {method === "razorpay" ? "Payment successful" : "Request submitted"}
              </div>
              <div className="text-sm mt-1 opacity-80">
                {method === "razorpay"
                  ? `${rupeesToCoins(amount)} coins have been added to your wallet.`
                  : `You'll receive ${rupeesToCoins(amount)} coins as soon as an admin verifies your payment (usually within a few hours).`}
              </div>
            </div>
            <Button onClick={onClose} className="w-full bg-ink text-white hover:bg-ink/90">Done</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input data-testid="top-up-amount" type="number" min={10} value={amount} onChange={(e) => setAmount(Math.max(10, parseInt(e.target.value, 10) || 10))} />
              <div className="text-[11px] text-ink/50 mt-1">You&apos;ll receive <b>{rupeesToCoins(amount)} coins</b> — enough for {Math.floor(rupeesToCoins(amount) / (settings.report_cost_coins || 15))} smart report{Math.floor(rupeesToCoins(amount) / (settings.report_cost_coins || 15)) === 1 ? "" : "s"}.</div>
            </div>

            {/* Preset amount chips — sourced from admin-configured recharge packs */}
            {(() => {
              const packs = settings.pricing?.recharge_packs || [];
              const fallback = [{ id: "p1", amount: 100 }, { id: "p2", amount: 300 }, { id: "p3", amount: 500 }, { id: "p4", amount: 1000 }];
              const list = packs.length ? packs : fallback;
              return (
                <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${Math.min(list.length, 4)}, minmax(0, 1fr))` }}>
                  {list.slice(0, 8).map((p) => (
                    <button
                      key={p.id || p.amount}
                      type="button"
                      data-testid={`top-up-preset-${p.amount}`}
                      onClick={() => setAmount(p.amount)}
                      className={`relative text-[12px] rounded-xl py-2 border transition-all ${amount === p.amount ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-border hover:border-ink"}`}
                    >
                      <div className="font-semibold">₹{p.amount}</div>
                      {p.bonus_credits > 0 && (
                        <div className={`text-[9px] uppercase tracking-wider mt-0.5 ${amount === p.amount ? "text-efgh-yellow" : "text-efgh-orange"}`}>+{p.bonus_credits} bonus</div>
                      )}
                      {p.popular && <span className="absolute -top-2 right-1 bg-efgh-orange text-white text-[9px] px-1.5 rounded-full font-semibold">POPULAR</span>}
                    </button>
                  ))}
                </div>
              );
            })()}

            {/* Method tabs */}
            {razorpayEnabled && (
              <div className="grid grid-cols-2 gap-2 p-1 rounded-lg bg-muted/40" data-testid="topup-method-tabs">
                <button
                  type="button"
                  data-testid="topup-method-razorpay"
                  onClick={() => setMethod("razorpay")}
                  className={`text-sm rounded-md py-2 flex items-center justify-center gap-1.5 transition-all ${method === "razorpay" ? "bg-white shadow font-semibold text-ink" : "text-ink/60"}`}
                >
                  <Zap className="w-3.5 h-3.5" /> Instant · Razorpay
                </button>
                <button
                  type="button"
                  data-testid="topup-method-upi"
                  onClick={() => setMethod("upi")}
                  className={`text-sm rounded-md py-2 flex items-center justify-center gap-1.5 transition-all ${method === "upi" ? "bg-white shadow font-semibold text-ink" : "text-ink/60"}`}
                >
                  Manual UPI
                </button>
              </div>
            )}

            {/* Razorpay pay */}
            {method === "razorpay" && razorpayEnabled && (
              <div className="space-y-3">
                <div className="rounded-lg border border-border p-3 bg-gradient-to-br from-orange-50 to-white text-[11px] text-ink/70">
                  Pay instantly with UPI · Cards · Netbanking · Wallets. Your wallet is credited within seconds — no manual verification needed.
                </div>
                <Button
                  onClick={payRazorpay}
                  disabled={saving}
                  data-testid="top-up-razorpay-btn"
                  className="w-full bg-efgh-orange hover:bg-efgh-orange/90 text-white"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Pay ₹{amount} instantly
                </Button>
              </div>
            )}

            {/* UPI (manual) */}
            {(method === "upi" || !razorpayEnabled) && (
              upiId ? (
                <form onSubmit={submit} className="space-y-4">
                  <div className="p-4 rounded-lg border border-border bg-muted/30 text-center" data-testid="upi-qr-block">
                    <div className="text-[10px] uppercase tracking-widest text-ink/50">Pay via UPI</div>
                    {settings.upi_qr_url ? (
                      <img src={settings.upi_qr_url} alt="UPI QR" className="w-40 h-40 mx-auto mt-2 rounded" />
                    ) : (
                      <div className="mx-auto mt-2 w-40 h-40 grid place-items-center bg-white rounded"><QRCodeSVG value={payUrl} size={144} level="M" /></div>
                    )}
                    <div className="mt-3 flex items-center justify-center gap-2 text-sm">
                      <span className="text-ink/60">{upiName}:</span>
                      <span className="font-mono font-semibold text-ink">{upiId}</span>
                      <button type="button" onClick={copyUpi} className="text-ink/40 hover:text-ink" data-testid="copy-upi">{copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}</button>
                    </div>
                    <div className="text-[10px] text-ink/50 mt-1">Scan the QR or copy the UPI ID · Any UPI app works (GPay / PhonePe / Paytm / BHIM)</div>
                  </div>
                  <div>
                    <Label>UPI reference / UTR *</Label>
                    <Input data-testid="top-up-utr" value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. 412345678912" />
                    <div className="text-[10px] text-ink/50 mt-0.5">Find this in your UPI app&apos;s transaction receipt after paying.</div>
                  </div>
                  <div>
                    <Label>Notes (optional)</Label>
                    <Input data-testid="top-up-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Bulk credit for family reports" />
                  </div>
                  <Button type="submit" disabled={saving} data-testid="top-up-submit" className="w-full bg-ink hover:bg-ink/90 text-white">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}I&apos;ve paid — submit for verification</Button>
                </form>
              ) : (
                <div className="p-4 text-center bg-warning/10 text-warning rounded-lg text-sm">The platform admin hasn&apos;t set a UPI ID for manual top-ups yet. Please use the Razorpay option above.</div>
              )
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
